import { AccessArgs, AccessResult, CollectionConfig, FieldBase, GlobalConfig, Option } from 'payload'
import { User } from '@payload-types'
import { WorkspaceRoles } from '@utils/collections'
import { isDeveloping } from '@utils/constants'
import { each, shouldBeArray } from '@utils/objects'

export const GROUPS: Record<string, string> = {
  // INTRANET
  INTRANET_ADMIN: (isDeveloping() ? '176c38d1-542e-4844-8543-2f24520bb78f' : 'd68af6ba-9a5e-4c11-a6bc-e65787fcf9ab'),
  INTRANET_MOVIMENTACAO_FINANCEIRA: 'bbc53fb4-7fed-4530-8219-f4b4183a1e0f',
  // SHAREPOINT
  ADMINISTRATIVO_SHAREPOINT: 'bc9d0ca6-e367-42ce-8818-80f638489eb0',
  COMERCIAL_SHAREPOINT: '29fbac5a-866e-46cf-94f5-03add1a21737',
  CREDITO_SHAREPOINT: '7cfb001b-bb34-4034-bb59-7d7e26d71130',
  COMPLIANCE_SHAREPOINT: '76feba93-ccfd-4596-afd5-61fe71471306',
  CONTABILIDADE_SHAREPOINT: '41bbbb6b-7faa-4e5d-844e-9fe9d18bda71',
  CONTROLADORIA_SHAREPOINT: '7ceac2d6-0079-4db9-819e-089695085f01',
  DADOS_SHAREPOINT: 'fa456542-97a7-420c-ae9b-48d29656106b',
  DESENVOLVIMENTO_SHAREPOINT: '098fb91c-43f1-4020-988a-08345f9c034a',
  DIRETORIA_SHAREPOINT: '672bae8a-c730-4af2-8c74-f8fc8d9934f4',
  DISTRIBUICAO_SHAREPOINT: '8d076d06-dccb-4079-8659-02cfa8aaf838',
  ESTRUTURACAO_SHAREPOINT: 'ae2116a6-8305-4703-b1fb-05b1c53bd73e',
  FINANCEIRO_SHAREPOINT: 'f34ca596-41ae-484a-90fa-08aaf5eb65ec',
  GESTAO_SHAREPOINT: '8806fae7-e1ba-46d4-bf2a-28a667bdd6c3',
  HEADS_SHAREPOINT: '09ec73db-c5e1-4823-a88f-c339e336aecc',
  JURIDICO_SHAREPOINT: '5a1423ee-abc9-4c78-a403-cf437bef47a1',
  MARKETING_SHAREPOINT: '069da408-a768-4705-8e51-16ee9db6265b',
  OBRIGACOES_SHAREPOINT: 'feec2196-360a-4ca8-a2ba-61b097fda45c',
  PRECIFICACAO_SHAREPOINT: 'd9a59ab0-c42b-44b2-a412-142e9b8c7728',
  PROCESSOS_SHAREPOINT: '3a73890f-f05c-42ad-9f26-3a4ccd2d4236',
  RH_SHAREPOINT: 'bdbda5b0-6e63-4128-8b83-a8e9f5ad73b7',
  SUPORTE_SHAREPOINT: '283733bd-dcdf-43a6-a68d-4c7c519431a3',
  TI_SHAREPOINT: '7698e769-17b8-4106-847f-6b64f40418a6',
} as const;

export const PERMISSIONS: Record<string, PermissionData> = {
  FLOW_ORIGINATORS_UPDATE: {
    group: 'Flow',
    description: 'Alterar Originadores',
  },
  FLOW_ORIGINATORS_DELETE: {
    group: 'Flow',
    description: 'Excluir Originadores',
  },
} as const;

export type PermissionData = { group: string; description: string; };

export type Group = keyof typeof GROUPS;
export type Groups = Group[];

export type GroupList = Record<Group, string>;

export const GROUPS_FORTESEC: Groups = Object.keys(GROUPS).filter((key) => ! key.startsWith('INTRANET_'));

export type Permission = keyof typeof PERMISSIONS;
export type Permissions = Permission[];

/**
 * Parse groups from token
 *
 * @param groups - Groups from token
 * @returns Parsed groups
 */
export function parseGroupsFromToken(groups: string[]): GroupList {
  const parsed: GroupList = {};

  for (const groupName in GROUPS) {
    const groupKey = GROUPS[groupName];

    if (! groups.includes(groupKey)) {
      continue;
    }

    parsed[groupName] = groupKey;
  }

  return parsed;
}

/**
 * Return all group names as a select option
 *
 * @returns
 */
export function getGroupsAsOptions(): Option[] {
  const parseGroupName = (group: string): string => {
    return String(group).replace('SHAREPOINT', '').replace(/_/g, ' ');
  };

  return each(GROUPS_FORTESEC, (group: string) => ({ value: group, label: parseGroupName(group) }), true);
}

export type CreateFieldAccessOptions = {
  create?: HasAccessChallenge | false,
  read?: HasAccessChallenge | false,
  update?: HasAccessChallenge | false,
};

export type CreateAccessOptions = {
  create?: HasAccessChallenge | false,
  read?: HasAccessChallenge | 'public' | false,
  update?: HasAccessChallenge | false,
  delete?: HasAccessChallenge | false,
};

export type CreateGlobalAccessOptions = {
  read?: HasAccessChallenge | 'public' | false,
  update?: HasAccessChallenge | false,
  readVersions?: HasAccessChallenge | null |false,
};

export function createFieldAccess<T extends any>(
  { create, read, update }: CreateFieldAccessOptions = {},
  callback?: (operation: 'create' | 'read' | 'update', doc: T | Partial<T> | null, args: any) => boolean | Promise<boolean>
): Required<FieldBase>['access'] {
  return {
    create: async (args) => {
      const { req: { user } } = args;

      if (! user || user?.external || create === false || ! hasAccessTo(user, create)) {
        return false;
      }

      if (typeof callback === 'function') {
        return await callback('create', ((args.data?.doc || args?.data) || null), args);
      }

      return true;
    },
    read: async (args) => {
      const { req: { user } } = args;

      if (! user || user?.external || read === false || ! hasAccessTo(user, read)) {
        return false;
      }

      if (typeof callback === 'function') {
        return await callback('read', ((args.data?.doc || args?.data) || null), args);
      }

      return true;
    },
    update: async (args) => {
      const { req: { user } } = args;

      if (! user || user?.external || update === false || ! hasAccessTo(user, update)) {
        return false;
      }

      if (typeof callback === 'function') {
        return await callback('update', ((args.data?.doc || args?.data) || null), args);
      }

      return true;
    },
  };
}

/**
 * Create a Access object for CRUD.
 *
 * @param accessOptions Object with a group (or list of groups) to check access.
 * @param owner         Provide a field to check user.id as owner to have access.
 * @param callback      A callback to add specific logic after checking access by external / groups, but before admin / owner permissions. Return null to skip.
 *
 * @returns
 */
export function createAccess<T extends any>(
    { create, read, update, delete: deleteGroup = 'INTRANET_ADMIN' }: CreateAccessOptions = {},
    owner: FieldBase['name'] | null = null,
    callback?: (operation: 'create' | 'read' | 'update' | 'delete', doc: T | null, args: AccessArgs<any>) => Promise<AccessResult|null>
  ): Required<CollectionConfig<any>>['access'] {

  return {
    create: async (args) => {
      const { req: { user }, data } = args;

      if (create === false) {
        return false;
      }

      if (! user || user?.external || ! hasAccessTo(user, create)) {
        return false;
      }

      // Check callback after access but before admin / owner
      const response = (typeof callback === 'function') ? await callback('create', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      // Check admin / owner
      return isAdmin(user) || (! owner) || (data && (data[owner] || null) === user.id);
    },
    read: async (args) => {
      const { req: { user } } = args;
      const isPublic = read === 'public';

      if (read === false) {
        return false;
      }

      if (! isPublic && (! user || user?.external || ! hasAccessTo(user, read))) {
        return false;
      }

      // Check callback after access but before public / admin / owner
      const response = (typeof callback === 'function') ? await callback('read', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      if (isPublic) {
        return true;
      }

      if (! user) {
        return false;
      }

      // Check admin / owner
      if (! owner || isAdmin(user)) {
        return true;
      }

      return { [owner]: { equals: user.id } };
    },
    update: async (args) => {
      const { req: { user, data }, id } = args;

      if (update === false) {
        return false;
      }

      if (! user || user?.external || ! hasAccessTo(user, update) || ! id) {
        return false;
      }

      // Check callback after access but before admin / owner
      const response = (typeof callback === 'function') ? await callback('update', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      // Check admin / owner
      if (! owner || isAdmin(user)) {
        return true;
      }

      if (data && (data[owner] || false) && data[owner] !== user.id) {
        return false;
      }

      return { [owner]: { equals: user.id } };
    },
    delete: async (args) => {
      const { req: { user }, id } = args;

      if (deleteGroup === false) {
        return false;
      }

      if (! user || user?.external || ! hasAccessTo(user, deleteGroup) || ! id) {
        return false;
      }

      // Check callback after access but before admin / owner
      const response = (typeof callback === 'function') ? await callback('delete', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      // Check admin / owner
      if (! owner || isAdmin(user)) {
        return true;
      }

      return { [owner]: { equals: user.id } };
    },
  };
}

export function createGlobalAccess<T extends any>(
  options: CreateGlobalAccessOptions = {},
  callback?: (operation: 'read' | 'update' | 'readVersions', doc: T | null, args: AccessArgs<any>) => Promise<AccessResult|null>
): Required<GlobalConfig<any>>['access'] {
  const { read, update, readVersions = null } = options;

  const readCallback = async (args) => {
    const { req: { user } } = args;

    if (read === false || ! user || ! hasAccessTo(user, read)) {
      return false;
    }

    // Check callback after access but before admin
    const response = (typeof callback === 'function') ? await callback('read', ((args.data?.doc || args?.data) || null), args) : null;
    if (response !== null) {
      return response;
    }

    return true;
  };

  return {
    read: readCallback,
    update: async (args) => {
      const { req: { user } } = args;

      if (update === false || ! user || ! hasAccessTo(user, update)) {
        return false;
      }

      // Check callback after access but before admin / owner
      const response = (typeof callback === 'function') ? await callback('update', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      return true;
    },
    readVersions: async (args) => {
      const { req: { user } } = args;

      if (readVersions === null) {
        return readCallback(args);
      }

      if (readVersions === false || ! user || ! hasAccessTo(user, readVersions)) {
        return false;
      }

      // Check callback after access but before admin / owner
      const response = (typeof callback === 'function') ? await callback('readVersions', ((args.data?.doc || args?.data) || null), args) : null;
      if (response !== null) {
        return response;
      }

      return true;
    },
  };
}

export function createAccessForCreateOnly(allowAdmins: boolean = true): FieldBase['access'] {
  return {
    create: () => true,
    read: () => true,
    update: ({ req: { user } }) => {
      if (! allowAdmins || ! user || user?.external) {
        return false;
      }

      return isAdmin(user);
    },
  };
}

/**
 * Check a user is administrator
 */
export function isAdmin(user: User|null): boolean {
  const userGroups = Object.values(user?.groups || {});
  return userGroups.includes(GROUPS.INTRANET_ADMIN)
}

/**
 * Check a user has access to workspace:
 * - All 'WorkspaceRoles'.
 * - All groups from external cruds;
 */
export function hasAccessToWorkspace(user: User|null) {
  const groups = getWorkspaceGroups();
  return hasAccessTo(user, groups, false, true);
}

export function getWorkspaceGroups() {
  return [
    ...WorkspaceRoles,
    'COMERCIAL_SHAREPOINT',
    'DESENVOLVIMENTO_SHAREPOINT',
    'GESTAO_SHAREPOINT',
    'PRECIFICACAO_SHAREPOINT',
  ];
}

/**
 * Check a user has access to one or more groups
 */
export type HasAccessChallenge = ExplicityHasAccessChallenge | null;
export type ExplicityHasAccessChallenge = Group | Groups;

export function hasAccessAsHeadTo(user: User|null, challenge: HasAccessChallenge = null): boolean {
  if (! challenge) {
    challenge = [];
  }

  challenge = Array.isArray(challenge) ? challenge : [ challenge ];

  for (let i = 0; i < challenge.length; i++) {
    if (hasAccessTo(user, [challenge[i], 'HEADS_SHAREPOINT'], true)) {
      return true;
    }
  }

  return false;
}

export function hasAccessTo(
  user: User | null,
  challenge: HasAccessChallenge = null,
  checkAll: boolean = false,
  ignoreAdmin: boolean = false
): boolean {
  const userGroups = Object.values(user?.groups || {});

  if (! ignoreAdmin && isAdmin(user)) {
    return true;
  }

  if (challenge === null || challenge === 'FORTESEC') {
    challenge = GROUPS_FORTESEC;
  }

  if (! Array.isArray(challenge)) {
    challenge = [challenge];
  }

  for (let i = 0; i < challenge.length; i++) {
    const group = GROUPS[challenge[i]] || null;

    if (group === null) {
      // eslint-disable-next-line no-console
      console.error(`The ${challenge[i]} group is invalid.`); // commit:ignore-console
    }

    const includes = userGroups.includes(group);

    if (checkAll && !includes) {
      return false;
    }

    if (! checkAll && includes) {
      return true;
    }

    if (i === challenge.length - 1) {
      return includes;
    }
  }

  return false;
}

export function hasPermissionTo(
  user: User | null,
  challenge: Permission,
  ignoreAdmin: boolean = false
): boolean {
  const userPermissions = shouldBeArray(user?.permissions ?? []);

  if (! ignoreAdmin && isAdmin(user)) {
    return true;
  }

  if (! Object.keys(PERMISSIONS).includes(challenge)) {
    // eslint-disable-next-line no-console
    console.error(`The ${challenge} permission is invalid.`); // commit:ignore-console
  }

  return userPermissions.includes(challenge);
}
